import React, { useRef, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  useToast
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getImportTemplate, importWorkbook } from "../../../api/clientsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { FieldLabel } from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX
} from "../../../components/Portal/portalModalChrome";

const TABS = ["company", "client", "need", "consultant"];

const ImportWorkbookModal = ({ isOpen, onClose, onSuccess }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [file, setFile] = useState(null);
  const [defaultManagerEmail, setDefaultManagerEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [report, setReport] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { t, i18n } = useTranslation("portal");

  const reset = () => {
    setFile(null);
    setReport(null);
    setDefaultManagerEmail("");
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleFilesSelected = (event) => {
    const selected = event.target.files?.[0] || null;
    setFile(selected);
    // A new file means a new run; clear the previous summary.
    setReport(null);
    event.target.value = null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
      setReport(null);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await getImportTemplate({
        buId: selectedBusinessUnitId || undefined,
        lang: i18n.resolvedLanguage || i18n.language
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "asap_pilot_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: t("pilot.import.templateError", { defaultValue: "Failed to download template." }),
        status: "error"
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    setIsSubmitting(true);
    try {
      const result = await importWorkbook(file, defaultManagerEmail || undefined, {
        buId: selectedBusinessUnitId || undefined
      });
      // Show the import summary and wait for the user to confirm (Done) before
      // closing, so they can review what was created / updated / deleted /
      // skipped and any errors.
      setReport(result);
      const hasErrors = TABS.some((tab) => (result?.[tab]?.errors || []).length > 0);
      toast({
        title: t("pilot.import.toasts.importCompleted", { defaultValue: "Workbook imported" }),
        status: hasErrors ? "warning" : "success"
      });
      onSuccess?.(result);
    } catch (e) {
      toast({
        title: t("pilot.import.toasts.importFailedTitle", { defaultValue: "Import failed" }),
        description: e?.response?.data?.detail || e?.response?.data?.message || "",
        status: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabLabel = (tab) =>
    t(`pilot.import.summary.${tab}`, {
      defaultValue:
        tab === "company"
          ? "Companies"
          : tab === "client"
          ? "Contacts"
          : tab === "need"
          ? "Needs"
          : "Consultants"
    });

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {t("pilot.import.uploadWorkbookTitle", { defaultValue: "Import workbook" })}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py="16px">
          <Text fontSize="13px" color="portal.muted" sx={{ marginBottom: "14px !important" }}>
            {t("pilot.import.workbookDescription", {
              defaultValue:
                "Upload a 5-tab workbook (Summary / Company / Client / Need / Consultant). The Summary tab is informational and is skipped."
            })}
          </Text>
          <HStack mb="14px" justify="flex-start">
            <Button
              leftIcon={<DownloadIcon />}
              size="sm"
              variant="secondary"
              onClick={handleDownloadTemplate}
              isLoading={downloadingTemplate}
              data-testid="download-template-in-modal"
            >
              {t("pilot.import.downloadTemplate", { defaultValue: "Download template" })}
            </Button>
          </HStack>
          <Box mb="14px">
            <FieldLabel isRequired>
              {t("pilot.import.workbookFileLabel", { defaultValue: "Workbook (.xlsx)" })}
            </FieldLabel>
            {file ? (
              <Tag size="md" variant="subtle" colorScheme="blue" borderRadius="999px">
                <TagLabel>{file.name}</TagLabel>
                <TagCloseButton onClick={() => { setFile(null); setReport(null); }} />
              </Tag>
            ) : (
              <Box
                border="1.5px dashed"
                borderColor={dragActive ? "portal.primary" : "portal.line"}
                borderRadius="10px"
                p="24px"
                textAlign="center"
                minH="160px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={dragActive ? "portal.active" : "portal.soft"}
                transition="all 0.15s"
                cursor="pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                _hover={{ bg: "portal.active", borderColor: "portal.primary" }}
                data-testid="workbook-dropzone"
              >
                <VStack spacing="8px">
                  <Icon as={FaCloudUploadAlt} boxSize={7} color="portal.primary" />
                  <Text fontSize="13px" fontWeight="700" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                    {t("pilot.import.dropzoneText", {
                      defaultValue: "Drop the .xlsx workbook here or click to browse"
                    })}
                  </Text>
                  <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {t("pilot.import.dropzoneAccepted", {
                      defaultValue: "Only .xlsx files are accepted."
                    })}
                  </Text>
                </VStack>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFilesSelected}
                  display="none"
                  isRequired
                  aria-required={true}
                />
              </Box>
            )}
          </Box>
          <Box mb="14px">
            <FieldLabel>
              {t("pilot.import.defaultManagerEmailLabel", { defaultValue: "Default manager email" })}
            </FieldLabel>
            <Input
              size="sm"
              type="email"
              value={defaultManagerEmail}
              onChange={(e) => setDefaultManagerEmail(e.target.value)}
              placeholder={t("pilot.import.defaultManagerEmailPlaceholder", {
                defaultValue: "manager@company.com"
              })}
            />
          </Box>

          {report && (
            <Box mt="16px" data-testid="import-summary">
              <Text
                fontSize="10px"
                textTransform="uppercase"
                fontWeight="800"
                letterSpacing="0.055em"
                color="portal.muted"
                sx={{ marginBottom: "10px !important" }}
              >
                {t("pilot.import.summaryTitle", { defaultValue: "Import summary" })}
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px" mb="12px">
                {TABS.map((tab) => {
                  const tabReport = report[tab] || {};
                  const stats = [
                    { key: "added", label: t("pilot.import.added", { defaultValue: "Added" }) },
                    { key: "updated", label: t("pilot.import.updated", { defaultValue: "Updated" }) },
                    { key: "deleted", label: t("pilot.import.deleted", { defaultValue: "Deleted" }) },
                    { key: "duplicates", label: t("pilot.import.duplicates", { defaultValue: "Duplicates" }) },
                    { key: "skipped", label: t("pilot.import.skipped", { defaultValue: "Skipped" }) },
                  ];
                  return (
                    <Box
                      key={tab}
                      border="1px solid"
                      borderColor="portal.line"
                      borderRadius="10px"
                      bg="portal.panel"
                      p="12px"
                      data-testid={`tab-summary-${tab}`}
                    >
                      <HStack justify="space-between" mb="8px">
                        <Text fontSize="13px" fontWeight="800" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                          {tabLabel(tab)}
                        </Text>
                        <HStack spacing="6px">
                          {tabReport.warnings && tabReport.warnings.length > 0 && (
                            <Badge colorScheme="orange">{tabReport.warnings.length}</Badge>
                          )}
                          {tabReport.errors && tabReport.errors.length > 0 && (
                            <Badge colorScheme="red">{tabReport.errors.length}</Badge>
                          )}
                        </HStack>
                      </HStack>
                      <VStack spacing="4px" align="stretch">
                        {stats.map(({ key, label }) => (
                          <HStack key={key} justify="space-between" fontSize="12px">
                            <Text color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                              {label}
                            </Text>
                            <Text fontWeight="700" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                              {tabReport[key] || 0}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  );
                })}
              </SimpleGrid>
              {TABS.some((tab) => (report[tab]?.errors || []).length > 0) && (
                <Accordion
                  allowMultiple
                  mt="12px"
                  sx={{
                    "& .chakra-accordion__item": {
                      border: "1px solid",
                      borderColor: "portal.line",
                      borderRadius: "10px",
                      mb: "8px",
                      overflow: "hidden",
                    },
                    "& .chakra-accordion__button": {
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "portal.text",
                    },
                  }}
                >
                  {TABS.map((tab) => {
                    const errors = report[tab]?.errors || [];
                    if (errors.length === 0) return null;
                    return (
                      <AccordionItem key={tab}>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            {tabLabel(tab)}: {t("pilot.import.errors", { defaultValue: "Errors" })} ({errors.length})
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb="12px">
                          <VStack align="stretch" spacing="4px">
                            {errors.map((err, i) => (
                              <Text key={i} fontSize="11px" color="portal.danger" sx={{ marginBottom: "0 !important" }}>
                                {t("pilot.import.errorRow", { defaultValue: "Row" })} {err.row || "?"}: {err.message}
                              </Text>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
              {TABS.some((tab) => (report[tab]?.warnings || []).length > 0) && (
                <Accordion
                  allowMultiple
                  mt="12px"
                  data-testid="import-warnings"
                  sx={{
                    "& .chakra-accordion__item": {
                      border: "1px solid",
                      borderColor: "portal.line",
                      borderRadius: "10px",
                      mb: "8px",
                      overflow: "hidden",
                    },
                    "& .chakra-accordion__button": {
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "portal.text",
                    },
                  }}
                >
                  {TABS.map((tab) => {
                    const warnings = report[tab]?.warnings || [];
                    if (warnings.length === 0) return null;
                    return (
                      <AccordionItem key={tab}>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            {tabLabel(tab)}: {t("pilot.import.skippedRows", { defaultValue: "Skipped" })} ({warnings.length})
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb="12px">
                          <VStack align="stretch" spacing="4px">
                            {warnings.map((warn, i) => (
                              <Text key={i} fontSize="11px" color="orange.500" sx={{ marginBottom: "0 !important" }}>
                                {t("pilot.import.errorRow", { defaultValue: "Row" })} {warn.row || "?"}: {warn.message}
                              </Text>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          {report ? (
            // After an import, the only action is to acknowledge the summary and
            // close the modal.
            <Button
              variant="primary"
              size="sm"
              onClick={handleClose}
              data-testid="confirm-import-summary"
            >
              {t("pilot.import.buttons.done", { defaultValue: "Done" })}
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={handleClose}>
                {t("pilot.import.buttons.close", { defaultValue: "Close" })}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                isDisabled={!file}
                isLoading={isSubmitting}
                data-testid="submit-workbook"
              >
                {t("pilot.import.buttons.import", { defaultValue: "Import" })}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImportWorkbookModal;
